import React, { useState } from "react";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Alert,
    CircularProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip
} from "@mui/material";
import {
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    AccountBalance as AccountIcon,
    TrendingUp as TrendingIcon,
    Receipt as ReceiptIcon,
    Verified as VerifiedIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { verifyFinancialSystem } from "../utils/financialVerification";

const FinancialVerification = ({ userId, open, onClose }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [summary, setSummary] = useState(null);

    const handleVerify = async () => {
        setLoading(true);
        setResults([]);
        setSummary(null);

        try {
            const verificationResults = await verifyFinancialSystem(userId);
            setResults(verificationResults);

            const passed = verificationResults.filter(r => r.passed).length;
            const total = verificationResults.length;

            setSummary({
                total,
                passed,
                failed: total - passed,
                successRate: total > 0 ? (passed / total) * 100 : 0
            });
        } catch (error) {
            setResults([{
                test: t("financialVerification.verificationProcess", "Verification Process"),
                passed: false,
                details: error.message,
                timestamp: new Date().toISOString()
            }]);
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (passed) => (
        passed ? <CheckIcon color="success" /> : <ErrorIcon color="error" />
    );

    const getTestIcon = (testName) => {
        switch (testName.toLowerCase()) {
        case "user profile":
            return <AccountIcon />;
        case "balance calculations":
            return <AccountIcon />;
        case "investment system":
            return <TrendingIcon />;
        case "transaction system":
            return <ReceiptIcon />;
        case "financial consistency":
            return <VerifiedIcon />;
        default:
            return <AccountIcon />;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <VerifiedIcon color="primary" />
                    <Typography variant="h6">
                        {t("financialVerification.title", "Financial System Verification")}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t(
                        "financialVerification.description",
                        "This verification checks the integrity of your financial data, including account balances, investments, and transaction history."
                    )}
                </Typography>

                {!results.length && !loading && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {t(
                            "financialVerification.prompt",
                            "Click \"Run Verification\" to check your financial data integrity."
                        )}
                    </Alert>
                )}

                {loading && (
                    <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                        <CircularProgress size={20} />
                        <Typography>
                            {t("financialVerification.running", "Running financial verification...")}
                        </Typography>
                    </Box>
                )}

                {summary && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            {t("financialVerification.summary", "Verification Summary")}
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                            <Chip
                                label={`${t("financialVerification.total", "Total")}: ${summary.total}`}
                                color="primary"
                                variant="outlined"
                            />
                            <Chip
                                label={`${t("financialVerification.passed", "Passed")}: ${summary.passed}`}
                                color="success"
                                variant="outlined"
                            />
                            <Chip
                                label={`${t("financialVerification.failed", "Failed")}: ${summary.failed}`}
                                color={summary.failed > 0 ? "error" : "default"}
                                variant="outlined"
                            />
                            <Chip
                                label={`${t("financialVerification.successRate", "Success Rate")}: ${summary.successRate.toFixed(1)}%`}
                                color={summary.successRate === 100 ? "success" : "warning"}
                                variant="outlined"
                            />
                        </Box>
                    </Box>
                )}

                {results.length > 0 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {t("financialVerification.results", "Verification Results")}
                        </Typography>
                        <List>
                            {results.map((result, index) => (
                                <ListItem
                                    key={index}
                                    sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 1 }}
                                    alignItems="flex-start"
                                >
                                    <ListItemIcon>
                                        {getTestIcon(result.test)}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="subtitle1" component="span">
                                                    {t(`financialVerification.tests.${result.test}`, result.test)}
                                                </Typography>
                                                {getStatusIcon(result.passed)}
                                            </Box>
                                        }
                                        secondaryTypographyProps={{ component: "div" }}
                                        secondary={
                                            <>
                                                <Typography variant="body2" color="text.secondary">
                                                    {result.details}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date(result.timestamp).toLocaleString()}
                                                </Typography>
                                            </>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {summary && summary.successRate === 100 && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        {t(
                            "financialVerification.allPassed",
                            "🎉 All financial verifications passed! Your account data is consistent and accurate."
                        )}
                    </Alert>
                )}

                {summary && summary.failed > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {t(
                            "financialVerification.someFailed",
                            "⚠️ Some verifications failed. Please contact support if you notice any discrepancies in your account balance or investment data."
                        )}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    {t("common.close", "Close")}
                </Button>
                <Button
                    onClick={handleVerify}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : <VerifiedIcon />}
                >
                    {loading
                        ? t("financialVerification.verifying", "Verifying...")
                        : t("financialVerification.run", "Run Verification")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FinancialVerification;